import type { ActionType, OperatingLoad, PermissionAudience } from "./types";

export type SuiteLayerKind = "universal" | "equipment" | "commodity" | "regulatory" | "customer_sop";
export type ValidatorStatus = "pass" | "inform" | "warn" | "block" | "override";
export type SuiteEventType = "create" | "tender" | "pickup" | "in_transit" | "delivery" | "billing";

export interface SuiteLayerRef {
  kind: SuiteLayerKind;
  key: string;
}

export interface ValidatorOverrideTarget {
  suiteId: string;
  validatorName: string;
}

export interface ValidatorResult {
  id: string;
  suiteId: string;
  validator: string;
  layer: SuiteLayerRef;
  status: ValidatorStatus;
  reason: string;
  overrides?: ValidatorOverrideTarget[];
}

export interface SuiteValidator {
  name: string;
  evaluate(load: OperatingLoad): ValidatorResult | Promise<ValidatorResult>;
}

export interface SuiteTool {
  name: string;
  actionType: ActionType;
  audience: PermissionAudience;
  eventTypes: SuiteEventType[];
  specificity: number;
  supersedes: string[];
}

export interface SuiteCadence {
  checkCallMinutes?: number;
  telemetryMinutes?: number;
  escalationMinutes?: number;
  notificationChannels?: string[];
  escalationTiers?: string[];
}

export interface AutomationSuite {
  id: string;
  layer: SuiteLayerRef;
  namespace: string;
  version: string;
  validators: SuiteValidator[];
  tools: SuiteTool[];
  exceptionTypes: string[];
  cadence?: SuiteCadence;
}

const suites = new Map<string, AutomationSuite>();

export function registerSuite(suite: AutomationSuite): void {
  validateSuiteNamespace(suite);
  suites.set(suite.id, suite);
}

export function clearSuiteRegistryForTests(): void {
  suites.clear();
}

export function listSuites(): AutomationSuite[] {
  return [...suites.values()];
}

export function resolveSuitesForLoad(load: OperatingLoad): AutomationSuite[] {
  const layerRefs = loadLayerRefs(load);
  return layerRefs.flatMap((layerRef) =>
    listSuites().filter((suite) => sameLayer(suite.layer, layerRef)),
  );
}

export function isRegisteredExceptionType(type: string): boolean {
  return listSuites().some((suite) => suite.exceptionTypes.includes(type));
}

export function loadLayerRefs(load: OperatingLoad): SuiteLayerRef[] {
  return [
    { kind: "universal", key: "core" },
    ...(load.equipment?.type ? [{ kind: "equipment" as const, key: load.equipment.type }] : []),
    ...(load.commodity?.category
      ? [{ kind: "commodity" as const, key: load.commodity.category }]
      : []),
    ...(load.regulatoryTags ?? []).map((tag) => ({ kind: "regulatory" as const, key: tag })),
  ];
}

function sameLayer(left: SuiteLayerRef, right: SuiteLayerRef): boolean {
  return left.kind === right.kind && left.key === right.key;
}

function validateSuiteNamespace(suite: AutomationSuite): void {
  const expectedPrefix = namespacePrefix(suite.layer);
  if (!suite.namespace.startsWith(expectedPrefix)) {
    throw new Error(`Suite ${suite.id} namespace must start with ${expectedPrefix}`);
  }

  for (const exceptionType of suite.exceptionTypes) {
    if (!exceptionType.startsWith(`${suite.namespace}.`)) {
      throw new Error(`Exception ${exceptionType} must be under namespace ${suite.namespace}`);
    }
  }
}

function namespacePrefix(layer: SuiteLayerRef): string {
  if (layer.kind === "universal") return "core";
  if (layer.kind === "equipment") return `equipment.${layer.key}`;
  if (layer.kind === "commodity") return `commodity.${layer.key}`;
  if (layer.kind === "regulatory") return `regulatory.${layer.key}`;
  return `sop.${layer.key}`;
}
